/**
 * @fileoverview The season's milestone asks, on Your Seasons: "Your season so far: 14
 * plants, first harvest Jun 12. Share it?" at the first harvest Grow
 * recognises, one around midsummer, and "Your 2026 season is ready" once the
 * season has ended. Which ask is due, and its words, are the backend's
 * (the season brief's due, from the tips' marks on the account).
 *
 * Asked as the first-photo "Share your {plant}?" is (ShareMoments): a quiet
 * panel from the bottom, Not now or Share…, said once. On Your Seasons
 * because the season, like the plant, is asked about where it lives: the
 * plant's on its card, the season's beside its entry, which Share… opens.
 *
 * Each is asked once per season: shown, taken or put away, it is kept on the
 * account with the tips' marks (useTips markKey, the season's year as the
 * scope, under the key the brief names), so a phone and a laptop agree. Only
 * on the gardener's own garden, and one ask per visit: what stays here is
 * which ask was answered this visit, and holding the ask on screen while it
 * is answered.
 *
 * Where: Your Seasons (SeasonPage), under the review entry.
 * API: none of its own; marks go through the tips (POST /tips/:key/:mark
 * with the year as scope).
 * Words (en/es): share.moment.notNow; the title, body, button and "asked
 * once" line are the brief's.
 *
 * @module SeasonMoments
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { BottomPanel } from '@/shared/components/ui/bottom-panel';
import { Button } from '@/shared/components/ui/button';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { useTips } from '@/features/tips/useTips';
import type { SeasonDue } from '@/shared/api/season';

/** An ask answered in this visit: no second one until the next visit. */
let answeredThisVisit = false;

/** What the asks decide from. */
interface SeasonMomentsProps {
  /** The ask due now (the brief's due), or null for none. */
  due: SeasonDue | null;
}

/**
 * The one milestone ask due now, if any, as a panel.
 *
 * @param props.due - The ask due now, or null.
 */
export const SeasonMoments: React.FC<SeasonMomentsProps> = ({ due }) => {
  const { reactTranslate: t } = useTranslation();
  const tips = useTips();
  const navigate = useNavigate();
  /** The ask on screen, held while it is answered so it does not change under the gardener's thumb. */
  const [held, setHeld] = React.useState<SeasonDue | null>(null);

  React.useEffect(() => {
    // Not while tending someone else's garden, nor before the tips know whose garden this is, nor twice a visit.
    if (held || !due || answeredThisVisit || tips.tending !== false) return;
    setHeld(due);
    tips.markKey(due.key, due.year, 'shown');
  }, [due, held, tips]);

  /**
   * The ask answered: Share… opens the review; Not now (or closing the
   * panel) puts it away for good.
   *
   * @param share - Whether the gardener chose to see and share it.
   */
  const answer = (share: boolean): void => {
    if (!held) return;
    answeredThisVisit = true;
    tips.markKey(held.key, held.year, share ? 'done' : 'dismissed');
    setHeld(null);
    if (share) navigate(`/season/review?year=${held.year}`);
  };

  if (!held) return null;

  return (
    <BottomPanel open onOpenChange={(open) => { if (!open) answer(false); }} title={held.title.text} wrapTitle>
      <p className="text-sm text-muted-foreground">{held.body.text}</p>
      <div className="mt-4 flex gap-2">
        <Button variant="outline" className="flex-1" onClick={() => answer(false)}>{t('share.moment.notNow')}</Button>
        <Button className="flex-1" onClick={() => answer(true)}>{held.button.text}</Button>
      </div>
      <p className="mt-2 text-center text-xs text-muted-foreground">{held.once.text}</p>
    </BottomPanel>
  );
};
